import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarDays, Check, Clock, Info, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { api, formatApiError } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { trackEvent } from "../lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Book() {
  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  }, []);

  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [dayMeta, setDayMeta] = useState(null);
  const [hour, setHour] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [paying, setPaying] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const fetchAvailability = useCallback(async (d) => {
    setLoadingAvail(true);
    setHour(null);
    try {
      const r = await api.get(`/appointments/availability?date=${toISODate(d)}`);
      setSlots(r.data.slots || []);
      setDayMeta(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load slots");
      setSlots([]);
    } finally {
      setLoadingAvail(false);
    }
  }, []);

  useEffect(() => {
    if (date) fetchAvailability(date);
  }, [date, fetchAvailability]);

  const canPay =
    date && hour != null && form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && !paying;

  const handlePay = async () => {
    if (!canPay) return;
    setPaying(true);
    try {
      const okScript = await loadRazorpay();
      if (!okScript) throw new Error("Failed to load Razorpay checkout");

      const { data: order } = await api.post("/appointments/order", {
        date: toISODate(date),
        hour,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim(),
      });
      trackEvent("booking_started", { slot: order.slot_label, amount: 3000, currency: "INR" });

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Blue Green Studio",
        description: `Consultation slot · ${order.slot_label}`,
        order_id: order.order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        notes: { appointment_id: order.appointment_id },
        theme: { color: "#2C6B4A" },
        handler: async (res) => {
          try {
            const verify = await api.post("/appointments/verify", {
              appointment_id: order.appointment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setConfirmed(verify.data.appointment);
            trackEvent("booking_completed", {
              appointment_id: order.appointment_id,
              slot: order.slot_label,
              value: 3000,
              currency: "INR",
              transaction_id: res.razorpay_payment_id,
            });
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: async () => {
            try {
              await api.post("/appointments/cancel", { appointment_id: order.appointment_id });
            } catch {
              // ignore
            }
            setPaying(false);
            if (date) fetchAvailability(date);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Could not start payment");
      setPaying(false);
    }
  };

  if (confirmed)
    return (
      <div className="min-h-screen bg-sand-dark/40 pt-32 pb-16 px-6" data-testid="booking-confirmed">
        <Seo title="Consultation Booked" path="/book" noIndex />
        <div className="max-w-2xl mx-auto bg-sand border border-ink/10 p-10 md:p-14 text-center">
          <div className="w-16 h-16 rounded-full bg-forest text-sand flex items-center justify-center mx-auto mb-8">
            <Check size={28} />
          </div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-4">Confirmed</p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink leading-tight">
            Your consultation is booked
          </h1>
          <div className="mt-8 grid grid-cols-2 gap-6 text-left">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone mb-1">Date</p>
              <p className="font-serif text-2xl text-ink">
                {new Date(confirmed.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone mb-1">Time (IST)</p>
              <p className="font-serif text-2xl text-ink">
                {String(confirmed.hour).padStart(2, "0")}:00 – {String(confirmed.hour + 1).padStart(2, "0")}:00
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone mb-1">Reference</p>
              <p className="text-ink font-mono text-sm">{confirmed.id}</p>
            </div>
          </div>
          <p className="mt-8 text-stone text-sm leading-relaxed">
            A member of Blue Green Studio will call {form.email} on the number below to confirm the format (studio visit, site walk or video call).
          </p>
          <Link
            to="/"
            data-testid="booking-home-link"
            className="inline-block mt-10 border border-ink/20 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-ink hover:text-sand transition-colors duration-500"
          >
            Back to home
          </Link>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen pt-32 md:pt-40 pb-24 md:pb-32" data-testid="book-page">
      <Seo
        title="Book a Studio Consultation — ₹3,000/hour"
        description="Book a one-hour design consultation with Blue Green Studio in Bangalore. Monday to Friday, 9am–5pm IST. ₹3,000 per slot, paid securely via Razorpay."
        path="/book"
        keywords={[
          "book architect consultation Bangalore",
          "architect appointment booking",
          "Blue Green Studio consultation",
        ]}
      />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-6"
        >
          Book a Consultation
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="font-serif text-4xl sm:text-5xl lg:text-[5vw] leading-[0.95] tracking-tight text-ink"
        >
          One hour with the studio — <span className="italic text-stone">₹3,000</span>
        </motion.h1>
        <p className="mt-6 max-w-2xl text-stone text-base md:text-lg leading-relaxed">
          Pick a date and a one-hour slot between 9am and 5pm IST, Monday to Friday. Payment is secured through Razorpay — the slot is only confirmed once payment clears.
        </p>

        <div className="mt-12 md:mt-16 grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Calendar column */}
          <Reveal className="lg:col-span-5">
            <div className="bg-sand border border-ink/10 p-5 md:p-7">
              <div className="flex items-center gap-3 mb-4">
                <CalendarDays size={18} className="text-forest" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-ink">Pick a date</p>
              </div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                fromDate={today}
                toDate={maxDate}
                disabled={(d) => d.getDay() === 0 || d.getDay() === 6 || d < today}
                data-testid="booking-calendar"
                className="mx-auto"
              />
              <div className="mt-4 flex items-start gap-2 text-xs text-stone">
                <Info size={14} className="mt-0.5 shrink-0" />
                <p>Bookings open for the next 30 days · Monday to Friday · IST</p>
              </div>
            </div>
          </Reveal>

          {/* Slots + details column */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="bg-sand border border-ink/10 p-6 md:p-10">
              <div className="flex items-center gap-3 mb-5">
                <Clock size={18} className="text-forest" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-ink">Available slots</p>
              </div>
              {!date && (
                <p className="text-stone text-sm">Select a date on the left to see hourly slots.</p>
              )}
              {date && loadingAvail && (
                <p className="text-stone text-sm">Loading slots…</p>
              )}
              {date && !loadingAvail && dayMeta && !dayMeta.is_bookable_day && (
                <p className="text-stone text-sm">{dayMeta.weekday} is not a bookable day.</p>
              )}
              {date && !loadingAvail && dayMeta?.is_bookable_day && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {slots.map((s) => (
                    <button
                      key={s.hour}
                      type="button"
                      disabled={!s.available}
                      onClick={() => setHour(s.hour)}
                      data-testid={`slot-${s.hour}`}
                      className={`px-4 py-4 text-sm border transition-colors ${
                        hour === s.hour
                          ? "bg-forest text-sand border-forest"
                          : s.available
                          ? "bg-sand border-ink/15 text-ink hover:border-forest"
                          : "bg-sand-dark/30 border-ink/10 text-stone/50 cursor-not-allowed line-through"
                      }`}
                    >
                      {s.label.replace(" IST", "")}
                    </button>
                  ))}
                </div>
              )}

              {/* Details */}
              <div className="mt-10 pt-10 border-t border-ink/10 grid gap-4">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-ink">Your details</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    data-testid="book-name"
                    placeholder="Full name *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-sand border border-ink/15 px-5 py-3.5 outline-none focus:border-forest transition-colors"
                  />
                  <input
                    data-testid="book-email"
                    placeholder="Email *"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-sand border border-ink/15 px-5 py-3.5 outline-none focus:border-forest transition-colors"
                  />
                </div>
                <input
                  data-testid="book-phone"
                  placeholder="Phone (optional, we'll call to confirm)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-sand border border-ink/15 px-5 py-3.5 outline-none focus:border-forest transition-colors"
                />
                <textarea
                  data-testid="book-notes"
                  rows={3}
                  placeholder="What would you like to discuss? (optional)"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-sand border border-ink/15 px-5 py-3.5 outline-none focus:border-forest transition-colors resize-none"
                />
              </div>

              <div className="mt-10 pt-10 border-t border-ink/10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-2 text-xs text-stone">
                  <ShieldCheck size={14} /> Secure payment via Razorpay · Refundable up to 24h prior
                </div>
                <button
                  data-testid="book-pay-button"
                  onClick={handlePay}
                  disabled={!canPay}
                  className="bg-forest text-sand px-10 py-5 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-teal transition-colors duration-500"
                >
                  {paying ? "Opening payment…" : `Pay ₹${dayMeta?.price_inr || 3000} & Confirm`}
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
